import React, { useRef, useEffect } from 'react';
import { Filter, Search } from 'lucide-react';
import { FilterableColumn } from '../../utils/fmeaTableUtils';

interface FilterDropdownProps {
    column: FilterableColumn;
    options: string[];
    selected: Set<string>;
    onSelectAll: (column: FilterableColumn) => void;
    onFilterChange: (column: FilterableColumn, value: string) => void;
    onClose: () => void;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
    column,
    options,
    selected,
    onSelectAll,
    onFilterChange,
    onClose
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    return (
        <div ref={dropdownRef} className="absolute top-full left-0 mt-1 bg-card border border-border rounded-md shadow-xl z-50 p-3 w-64 max-h-[400px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id={`select-all-${column}`}
                        checked={selected.size === options.length && options.length > 0}
                        onChange={() => onSelectAll(column)}
                        className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                    />
                    <label htmlFor={`select-all-${column}`} className="font-bold text-sm text-text-primary cursor-pointer">
                        Select All
                    </label>
                </div>
                <span className="text-[10px] text-text-secondary uppercase font-bold tracking-wider">
                    {selected.size} / {options.length}
                </span>
            </div>

            <div className="flex-1 overflow-y-auto space-y-1.5 py-1">
                {options.length === 0 ? (
                    <div className="py-4 text-center text-xs text-text-secondary italic">
                        No options available
                    </div>
                ) : (
                    options.map(option => (
                        <div key={option} className="flex items-center gap-2 hover:bg-background rounded px-1 transition-colors">
                            <input
                                type="checkbox"
                                id={`${column}-${option}`}
                                value={option}
                                checked={selected.has(option)}
                                onChange={() => onFilterChange(column, option)}
                                className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                            />
                            <label htmlFor={`${column}-${option}`} className="text-sm text-text-primary truncate flex-1 cursor-pointer py-1" title={option}>
                                {option || <span className="italic text-text-secondary">(Empty)</span>}
                            </label>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
